/**
 * Auth store — current sign-in state for the React tree.
 *
 *   "unknown"   — initial; haven't called /api/auth/me yet
 *   "anonymous" — /me returned authenticated:false (or a network error)
 *   "authed"    — /me returned a user with email
 *
 * The store doesn't know about cookies — those are managed by the browser
 * and the api client. This is just the rendered view of identity.
 */
import { create } from "zustand";
import { api, type AuthedUser } from "@/api/client";

export type AuthStatus = "unknown" | "anonymous" | "authed";

interface AuthStore {
  status: AuthStatus;
  user: AuthedUser | null;
  loadMe: () => Promise<void>;
  setAuthed: (user: AuthedUser) => void;
  signOut: () => Promise<void>;
  /** Patch country override on the server and reflect it in store state. */
  updateCountry: (countryCode: string) => Promise<void>;
  /** Permanently delete the account. Cookies are cleared server-side; we
   *  flip status to anonymous and drop user state. */
  deleteAccount: () => Promise<void>;
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  status: "unknown",
  user: null,

  loadMe: async () => {
    try {
      const me = await api.me();
      if (me.authenticated && me.user) {
        set({ status: "authed", user: me.user });
      } else {
        set({ status: "anonymous", user: null });
      }
    } catch {
      set({ status: "anonymous", user: null });
    }
  },

  setAuthed: (user) => set({ status: "authed", user }),

  signOut: async () => {
    try { await api.signOut(); } catch { /* best-effort */ }
    set({ status: "anonymous", user: null });
  },

  updateCountry: async (countryCode) => {
    const result = await api.updateCountry(countryCode);
    const cur = get().user;
    if (cur) {
      set({ user: { ...cur, ...result.user } });
    }
  },

  deleteAccount: async () => {
    await api.deleteAccount();
    set({ status: "anonymous", user: null });
  },
}));
